import React from "react";
import { useTranslation } from "react-i18next";

import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Link, Stack, TableCell, TableRow, Typography } from "@mui/material";
import { CalendarHoliday } from "assets/material-custom-icons";
import { NavigateLink } from "components/Navigate-link/NavigateLink";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { PaymentStatus } from "constants/debt";
import { AmortizationColumn, AmortizationRow } from "store/types";

type AmortizationTableRowProps = {
  row: AmortizationRow;
  columns: AmortizationColumn[];
  showExpandCell?: boolean;
  showBorderBottom?: boolean;
  isChild?: boolean;
  expandButton?: React.ReactNode;
  onMenuOpen: (e: React.MouseEvent<HTMLElement>) => void;
  onSubDealClick: (e: React.MouseEvent<HTMLElement>, children: AmortizationRow[]) => void;
  onCellExplanationClick?: (cellKey: string, explanation: string, value: number, isCtrl: boolean) => void;
  selectedCellKeys?: string[];
  primaryCellKey?: string;
  formatCellValue: (value: any, column: any) => string | number;
};

export const AmortizationTableRow: React.FC<AmortizationTableRowProps> = React.memo(
  ({
    row,
    columns,
    showBorderBottom,
    isChild,
    showExpandCell,
    expandButton,
    onMenuOpen,
    onSubDealClick,
    onCellExplanationClick,
    selectedCellKeys,
    primaryCellKey,
    formatCellValue
  }) => {
    const { t } = useTranslation("debt");
    const termsChangeLabel = t("amortization.termsChange");
    const isTermsChange = !!row.description?.includes(termsChangeLabel);
    const showTermsIndicator = isTermsChange && showBorderBottom;

    // x-position of the boundary between the notes cell and the first data column
    const indicatorLeft = showExpandCell ? 118 : 78;

    const getRowBackgroundColor = () => {
      if (row.status === PaymentStatus.RESOLVED) {
        return "#d4edda";
      }
      if (row.status === PaymentStatus.MISMATCH) {
        return "#f8d7da";
      }
      return "transparent";
    };

    const stickyBg =
      row.status === PaymentStatus.RESOLVED ? "#d4edda" : row.status === PaymentStatus.MISMATCH ? "#f8d7da" : "#fff";
    const stickyBgHover =
      row.status === PaymentStatus.RESOLVED ? "#c3e6cb" : row.status === PaymentStatus.MISMATCH ? "#f5c6cb" : "#ECF1F8";

    return (
      <TableRow
        {...(showTermsIndicator ? { "data-terms-label": termsChangeLabel } : {})}
        sx={{
          bgcolor: getRowBackgroundColor(),
          ...(showTermsIndicator && {
            position: "relative",
            "&::after": {
              content: "attr(data-terms-label)",
              display: "block",
              position: "absolute",
              bottom: 0,
              left: indicatorLeft,
              transform: "translate(-50%, 50%)",
              zIndex: 3,
              backgroundColor: "#fff",
              border: "1px dashed #4caf50",
              borderRadius: "3px",
              padding: "2px 6px",
              fontSize: "10px",
              fontWeight: 500,
              color: "#4caf50",
              lineHeight: 1,
              whiteSpace: "nowrap",
              pointerEvents: "none"
            }
          }),
          "&:hover": {
            bgcolor:
              row.status === PaymentStatus.RESOLVED
                ? "#c3e6cb"
                : row.status === PaymentStatus.MISMATCH
                  ? "#f5c6cb"
                  : "#ECF1F8",
            "& .action-button": {
              opacity: 1
            },
            "& .sticky-col": {
              bgcolor: stickyBgHover
            }
          },
          "& .sticky-col": {
            bgcolor: stickyBg
          },
          borderBottom: showBorderBottom
            ? isTermsChange
              ? "1px dashed #4caf50"
              : "1px solid #DBE8F5"
            : "none",
          "& .MuiTableCell-root": {
            borderBottom: "none",
            pl: 2
          },
          color: new Date(row.date) < new Date() ? "#496682" : "#071F35"
        }}
      >
        {showExpandCell && (
          <TableCell
            className="sticky-col"
            sx={{ width: "40px", minWidth: "40px", padding: "6px 6px", position: "sticky", left: 0, zIndex: 2 }}
          >
            {expandButton}
          </TableCell>
        )}
        <TableCell
          className="sticky-col"
          sx={{
            width: "32px",
            minWidth: "32px",
            padding: "6px",
            textAlign: "center",
            verticalAlign: "middle",
            position: "sticky",
            left: showExpandCell ? 40 : 0,
            zIndex: 2
          }}
        >
          {row.notes && row.notes.length > 0 ? (
            <MaterialTooltip
              isShow
              title={
                <Stack spacing={0.5}>
                  {row.notes.map((note, index) => (
                    <Typography key={index} sx={{ fontSize: "12px" }}>
                      {note}
                    </Typography>
                  ))}
                </Stack>
              }
            >
              <InfoIcon
                sx={{ fontSize: 18, color: "#1976d2", display: "block", margin: "0 auto", cursor: "pointer" }}
              />
            </MaterialTooltip>
          ) : row.status === PaymentStatus.RESOLVED ? (
            <CheckIcon sx={{ fontSize: 18, color: "#28a745", display: "block", margin: "0 auto" }} />
          ) : row.status === PaymentStatus.MISMATCH ? (
            <MaterialTooltip isShow title="Payment has arrear">
              <ErrorIcon sx={{ fontSize: 18, color: "#dc3545", display: "block", margin: "0 auto" }} />
            </MaterialTooltip>
          ) : null}
        </TableCell>
        {columns.map((column, colIndex) => {
          const accrualExplanation = column.explanation_field
            ? row[column.explanation_field as keyof AmortizationRow] as string | null | undefined
            : null;

          return (
            <TableCell
              key={column.key}
              className={colIndex === 0 ? "sticky-col" : undefined}
              onClick={accrualExplanation ? (e) => onCellExplanationClick?.(
                `${row.segmentIds.join(",")}-${row.date}-${column.key}`,
                accrualExplanation,
                row[column.key as keyof AmortizationRow] as number,
                e.ctrlKey || e.metaKey
              ) : undefined}
              sx={{
                color: "inherit",
                verticalAlign: "middle",
                ...(accrualExplanation && onCellExplanationClick ? { cursor: "pointer" } : {}),
                ...(() => {
                  const cellKey = `${row.segmentIds.join(",")}-${row.date}-${column.key}`;
                  if (!selectedCellKeys?.includes(cellKey)) return {};
                  return { boxShadow: cellKey === primaryCellKey ? "inset 0 0 0 2px #1976d2" : "inset 0 0 0 2px #9e9e9e" };
                })(),
                ...(colIndex === 0
                  ? {
                      position: "sticky",
                      left: showExpandCell ? 72 : 32,
                      zIndex: 2
                    }
                  : {})
              }}
            >
              <Stack sx={{ flexDirection: "row", alignItems: "center", gap: "6px", color: "inherit" }}>
                {column.key === "sub_deal" && row.deal_id ? (
                  <NavigateLink
                    allowNewTab={true}
                    route="dealDashboard"
                    params={{ deal_id: String(row.deal_id) }}
                    style={{ color: "#26a6ae" }}
                  >
                    {row.sub_deal}
                  </NavigateLink>
                ) : column.key === "sub_deal" && !row.deal_id && row.children && row.children.length > 0 ? (
                  <Link
                    component="button"
                    onClick={(e) => onSubDealClick(e, row.children!)}
                    sx={{
                      color: "#26a6ae",
                      fontSize: "14px",
                      lineHeight: "16px",
                      cursor: "pointer",
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline"
                      }
                    }}
                  >
                    {row.sub_deal}
                  </Link>
                ) : (
                  <Typography sx={{ color: "inherit", fontSize: "14px", lineHeight: "16px" }}>
                    {column.key === "date" && isChild
                      ? ""
                      : formatCellValue(row[column.key as keyof AmortizationRow], column)}
                  </Typography>
                )}
                {row.is_holiday && column.key === "date" && (
                  <MaterialTooltip isShow title="Bank holiday in the US">
                    <CalendarHoliday fontSize="sm" />
                  </MaterialTooltip>
                )}
              </Stack>
            </TableCell>
          );
        })}
        <TableCell align="right">
          <IconButton
            size="sm"
            className="action-button"
            onClick={onMenuOpen}
            sx={{
              opacity: 0,
              transition: "opacity 0.2s"
            }}
          >
            <MoreVertIcon fontSize="sm" />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  }
);
